import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCosmosApi } from "@/lib/api/useCosmosApi";
import type {
	DeckArrangement,
	PresentationSettings
} from "@/lib/engineering-insights/presentation";

export type EngineeringInsightsGranularity = "week" | "month";

export interface EngineeringInsightsStatus {
	enabled: boolean;
}

export interface MetricSeriesRow {
	period: string;
	squad: string;
	engineer: string | null;
	segmentKey: string | null;
	segmentValue: string | null;
	value: number;
	aggregation: "sum" | "avg";
}

export interface EngineeringInsightsMetricsResponse {
	metricKey: string;
	granularity: EngineeringInsightsGranularity;
	rows: MetricSeriesRow[];
}

export interface EngineeringMetricsParams {
	from: string;
	to: string;
	squad?: string;
	granularity: EngineeringInsightsGranularity;
	includeOverlays?: boolean;
}

export interface EngineeringInsightsSummaryRequest {
	metricKey: string;
	title: string;
	description: string;
	valueLabel: string;
	from: string;
	to: string;
	squad?: string;
	granularity: EngineeringInsightsGranularity;
}

export interface EngineeringInsightsSummaryResponse {
	metricKey: string;
	summary: string;
	provider: "bedrock" | "fallback";
	model?: string;
}

export interface EngineeringRelatedDocumentsParams {
	metricKey: string;
	period?: string;
	from?: string;
	to?: string;
	granularity: EngineeringInsightsGranularity;
	squad?: string;
	engineer?: string;
	segmentValue?: string;
}

export interface EngineeringRelatedDocumentsResponse {
	metricKey: string;
	sourceType: "work_item" | "pull_request" | "ai_usage_daily";
	total: number;
	documents: Array<Record<string, unknown> & { id: string; type: string }>;
}

export function useEngineeringInsightsStatus() {
	const request = useCosmosApi();

	return useQuery({
		queryKey: ["engineering-insights-status"],
		queryFn: () => request<EngineeringInsightsStatus>("/api/engineering-insights/status"),
		staleTime: 5 * 60 * 1000
	});
}

export function useEngineeringMetrics(
	metricKey: string,
	{ from, to, squad, granularity, includeOverlays }: EngineeringMetricsParams
) {
	const request = useCosmosApi();
	const status = useEngineeringInsightsStatus();

	return useQuery({
		queryKey: [
			"engineering-insights-metrics",
			metricKey,
			from,
			to,
			squad ?? "all",
			granularity,
			includeOverlays === true
		],
		queryFn: () => {
			const search = new URLSearchParams({
				metricKey,
				dateFrom: from,
				dateTo: to,
				granularity
			});
			if (squad) {
				search.set("squad", squad);
			}
			if (includeOverlays) {
				search.set("includeOverlays", "true");
			}
			return request<EngineeringInsightsMetricsResponse>(
				`/api/engineering-insights/metrics?${search.toString()}`
			);
		},
		staleTime: 5 * 60 * 1000,
		enabled: status.data?.enabled === true
	});
}

const PRESENTATION_SETTINGS_QUERY_KEY = ["engineering-insights-presentation-settings"];

export function useEngineeringPresentationSettings() {
	const request = useCosmosApi();
	const status = useEngineeringInsightsStatus();

	return useQuery({
		queryKey: PRESENTATION_SETTINGS_QUERY_KEY,
		queryFn: () =>
			request<PresentationSettings>("/api/engineering-insights/presentation-settings"),
		staleTime: 60 * 1000,
		enabled: status.data?.enabled === true
	});
}

export function useUpdateEngineeringPresentationSettings() {
	const request = useCosmosApi();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (arrangement: DeckArrangement) =>
			request<PresentationSettings>("/api/engineering-insights/presentation-settings", {
				method: "PUT",
				body: JSON.stringify(arrangement)
			}),
		onSuccess: (settings) => {
			queryClient.setQueryData(PRESENTATION_SETTINGS_QUERY_KEY, settings);
		}
	});
}

export function useEngineeringChartSummary() {
	const request = useCosmosApi();

	return useMutation({
		mutationFn: (body: EngineeringInsightsSummaryRequest) =>
			request<EngineeringInsightsSummaryResponse>("/api/engineering-insights/summary", {
				method: "POST",
				body: JSON.stringify(body)
			})
	});
}

export function useEngineeringRelatedDocuments(
	params: EngineeringRelatedDocumentsParams | null,
	enabled: boolean
) {
	const request = useCosmosApi();

	return useQuery({
		queryKey: ["engineering-insights-documents", params],
		queryFn: () => {
			if (!params) {
				throw new Error("Missing related documents params.");
			}
			const search = new URLSearchParams({
				metricKey: params.metricKey,
				granularity: params.granularity
			});
			if (params.period) {
				search.set("period", params.period);
			}
			if (params.from && params.to) {
				search.set("dateFrom", params.from);
				search.set("dateTo", params.to);
			}
			if (params.squad) {
				search.set("squad", params.squad);
			}
			if (params.engineer) {
				search.set("engineer", params.engineer);
			}
			if (params.segmentValue) {
				search.set("segmentValue", params.segmentValue);
			}
			return request<EngineeringRelatedDocumentsResponse>(
				`/api/engineering-insights/documents?${search.toString()}`
			);
		},
		enabled: enabled && params !== null,
		staleTime: 60 * 1000
	});
}
